import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight, Sparkles, CheckCircle } from 'lucide-react';
import { subscriptionPlans, enterprisePlan, FAQList } from '../data/mockData';

const PricingPage = () => {
  const [billingCycle, setBillingCycle] = useState('monthly');

  const getPrice = (plan) => {
    if (plan.price === 'Free') return 'Free';
    if (plan.price === 'Custom') return 'Custom';

    // Return the appropriate price based on billing cycle
    if (billingCycle === 'annual') {
      return plan.priceAnnual || plan.price;
    }
    return plan.price;
  };

  const getDuration = (plan) => {
    if (plan.price === 'Free') return plan.duration;
    if (plan.price === 'Custom') return plan.duration;
    if (billingCycle === 'annual') return 'per user/month (billed annually)';
    return plan.duration;
  };

  const getSavings = (plan) => {
    if (plan.price === 'Free' || plan.price === 'Custom' || billingCycle === 'monthly') return null;
    if (!plan.priceAnnual) return null;

    const monthlyPrice = parseInt(plan.price.replace('$', '')) || 0;
    const annualPrice = parseInt(plan.priceAnnual.replace('$', '')) || 0;
    const savings = monthlyPrice - annualPrice;

    if (savings > 0) {
      return `Save $${savings}/month`;
    }
    return null;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in">
            <div className="flex items-center justify-start space-x-2 mb-6">
              <Sparkles className="h-6 w-6 text-blue-400" />
              <span className="text-blue-400 font-medium">Built to Scale</span>
              <Sparkles className="h-6 w-6 text-blue-400" />
            </div>
            <h1 className="display-lg text-white mb-6">
              Find the Right Fit for Your Team
            </h1>
            <p className="body-lg mb-8">
              Start with a full-featured trial and explore what GrowSquare can do. When you're ready, choose a plan that scales as your business grows.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-start space-x-4 mb-8" style={{ display: "none" }}>
              <span className={`body-md ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-400'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${billingCycle === 'annual' ? 'bg-blue-500' : 'bg-slate-600'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
              <div className="flex items-center space-x-2">
                <span className={`body-md ${billingCycle === 'annual' ? 'text-white' : 'text-slate-400'}`}>
                  Annual
                </span>
                {billingCycle === 'annual' && (
                  <Badge className="bg-green-500/10 text-green-400 text-xs">
                    Save nearly 10%
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="relative flex justify-center">
            <img
              src='/images/plansBanner.webp'
              alt="Hand ticking items on a digital checklist"
              loading="eager"
              className="w-full max-w-[420px] aspect-[4/5] object-cover rounded-xl border border-slate-700 shadow-2xl"
            />
          </div>
          </div>
        </div>
      </section>

      {/* Plans Plans */}
      <section className="section-padding">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {subscriptionPlans.map((plan, i) => (
              <Card key={i} className={`feature-card ${plan.highlight ? 'border-blue-500 bg-blue-500/5 scale-105' : ''}`}> {/*  h-fit */}
                <CardHeader>
                  <CardTitle className="h2 text-white text-center flex items-center justify-between space-x-2">{plan.name} {plan.highlight && (
                    <div className="bg-blue-500 text-white newtext-sm font-medium px-3 py-1 rounded-full w-fit">
                      Most Popular
                    </div>)}
                    {/* {plan.highlight && (
                    <div className="flex items-center justify-center mb-4">
                      <Badge className="bg-blue-500 text-white newtext-sm font-medium px-4 py-2">
                        <Star className="h-4 w-4 mr-1" />
                        Most Popular
                      </Badge>
                    </div>
                  )} */}
                  </CardTitle>
                  <div className="text-center mb-4" style={{ display: 'none' }}>
                    <div className="flex items-baseline justify-center space-x-2">
                      <span className="text-5xl font-bold text-blue-400">{getPrice(plan)}</span>
                      {getSavings(plan) && (
                        <Badge className="bg-green-500/10 text-green-400 text-xs">
                          {getSavings(plan)}
                        </Badge>
                      )}
                    </div>
                    {getDuration(plan) && <span className="text-slate-400 text-lg">{getDuration(plan)}</span>}
                  </div>
                  <CardDescription className="body-md "> {/* text-center */}
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Action Button */}
                  <div className="mb-8">
                    {plan.buttonAction === 'trial' ? (
                      <>
                        <Link to="/checkout?plan=Trial&billing=monthly">
                          {/* < Link to="/contact"> */}
                          <Button className="btn-primary w-full glow-effect">
                            {plan.buttonText}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </Link>
                      </>
                    ) : plan.buttonAction === 'contact' ? (
                      <Link to="/contact">
                        <Button className="btn-secondary w-full">
                          {plan.buttonText}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    ) : (
                      // <Link to={`/checkout?plan=${plan.id}&billing=${billingCycle}`}>
                      //   <Button className={`w-full ${plan.highlight ? 'btn-primary glow-effect' : 'btn-secondary'}`}>
                      //     {plan.buttonText}
                      //     <ArrowRight className="ml-2 h-5 w-5" />
                      //   </Button>
                      // </Link>
                      <Link to="/contact">
                        <Button className="w-full btn-primary glow-effect">
                          {plan.buttonText}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="body-md font-semibold text-white mb-4">
                      {plan.name === 'Professional' ? 'Everything in Standard, plus:' : plan.name === "Standard" ? "Basic Features Included:" : 'All Features Included:'}
                    </h4>

                    <div className="max-h-80 overflow-y-auto pr-2 space-y-2 ownScrollbar-custom">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start space-x-3">
                          <Check className="h-4 w-4 text-blue-400 mt-1 flex-shrink-0" />
                          <span className="body-sm text-slate-300 leading-relaxed">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {plan.features.length > 15 && (
                      <div className="pt-3 border-t border-slate-700">
                        <p className="body-sm text-blue-400 text-center">
                          {plan.features.length} total features included
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <Card className="feature-card mt-16">
            <CardHeader>
              <CardTitle className="h3 text-white">{enterprisePlan.name}</CardTitle>
              <CardDescription className="body-md">
                {enterprisePlan.subTitle1}
              </CardDescription>
              <CardDescription className="body-sm text-slate-400">
                {enterprisePlan.description1}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className='mb-6'>
                <h5 className="h5 text-white mb-3">Key Features:</h5>
                {enterprisePlan.features.map((plan, index) => (
                  <div key={index} className="flex items-start space-x-2 mb-3">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-1 flex-shrink-0" />
                    <span className="body-sm text-slate-300"><span className='text-white'>{plan.title}</span>{plan.content}</span>
                  </div>
                  // <div className="bg-green-400/10 rounded-lg p-4 mb-2" key={index}>
                  //   <p className="body-md font-semibold text-green-400"><span className='text-white'>{plan.title}</span>{plan.content}</p>
                  // </div>
                ))}
              </div>
              <CardDescription className="body-sm text-slate-400 mb-6">
                {enterprisePlan.contactUs1}
              </CardDescription>
              <Link to="/contact">
                <Button className="btn-primary">
                  {enterprisePlan.buttonText}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section >

      {/* Feature Comparison */}
      {/* <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="display-md text-white mb-6">
              Detailed Feature Comparison
            </h2>
            <p className="body-lg max-w-2xl mx-auto">
              Compare all features across our plans to find the perfect fit for your business needs.
            </p>
          </div>

          <Tabs defaultValue="core" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-3 bg-slate-800">
              <TabsTrigger value="core" className="data-[state=active]:bg-blue-500">Core CRM</TabsTrigger>
              <TabsTrigger value="advanced" className="data-[state=active]:bg-blue-500">Advanced</TabsTrigger>
              <TabsTrigger value="ai" className="data-[state=active]:bg-blue-500">AI & Analytics</TabsTrigger>
            </TabsList>
            
            <TabsContent value="core" className="mt-8">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Core CRM Features</CardTitle>
                  <CardDescription>Essential CRM functionality available in all plans</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      "Core CRM Modules",
                      "Customizable Views", 
                      "Personalized Dashboard",
                      "Advanced Search & Filters",
                      "Task Assignment & Tracking",
                      "Notes & Activity Logs",
                      "Document Storage & Management",
                      "Bulk Data Updates",
                      "Notifications & Reminders",
                      "Data Import & Export Tools"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-blue-400" />
                        <span className="body-sm text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="advanced" className="mt-8">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">Advanced Features</CardTitle>
                  <CardDescription>Powerful tools for scaling businesses</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      "Advanced Deal Tracking",
                      "Kanban View for Leads and Deals",
                      "Integrated Email (Gmail, Outlook, Zoho)",
                      "Calendar Synchronization", 
                      "Timeline View of Activities",
                      "Quotation Management",
                      "Sales Order Processing",
                      "Invoice Management",
                      "Multi-Factor Authentication",
                      "Role-Based Access Control"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-blue-400" />
                        <span className="body-sm text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ai" className="mt-8">
              <Card className="feature-card">
                <CardHeader>
                  <CardTitle className="h3 text-white">AI & Analytics</CardTitle>
                  <CardDescription>
                    <Badge className="bg-blue-500/10 text-blue-400 mr-2">Professional Only</Badge>
                    Intelligent automation and advanced analytics
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {[
                      "AI Lead Intelligence",
                      "Advanced Revenue Forecasting",
                      "Intelligent Scoring for Leads, Deals, and Accounts",
                      "Advanced Workflow Automation",
                      "Advanced Reporting & Analytics",
                      "Sales Target & Goal Tracking",
                      "Territory-Based Sales Management",
                      "Automated Assignment Rules",
                      "Custom Approval Workflows",
                      "Marketing Campaign Management"
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <Check className="h-4 w-4 text-blue-400" />
                        <span className="body-sm text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section> */}

      {/* FAQ Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="display-md text-white mb-6">
                Frequently Asked Questions
              </h2>
            </div>

            <div className="grid gap-8">
              {FAQList.map((plan, index) => (
                <Card className="feature-card" key={index}>
                  <CardContent className="pt-6">
                    <h3 className="h3 text-white mb-3">{plan.question}</h3>
                    <p className="body-md text-slate-300">
                      {plan.answer}
                    </p>
                  </CardContent>
                </Card>))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              Ready to Revolutionize Your CRM?
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Start with a complete 14-day trial of all Professional features. No credit card required.
              Experience the future of customer relationship management today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/checkout?plan=Trial&billing=monthly">
                {/* <Link to="/contact"> */}
                <Button className="btn-primary glow-effect">
                  Start Free Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-secondary">
                  Talk to Sales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div >
  );
};
export default PricingPage;